// =============================================================================
// src/chrome/settings-panel/index.ts — Painel de Configuracoes (real, minimo).
//
// Proveniencia: reforma institucional 2026-06 (Fase 4). "Configurações em
//   breve" (toast) era um tell de prototipo na conta. Painel minimo REAL:
//   bloco de identidade (topbar-identity) + 2 switches funcionais que toggam
//   body classes consumidas por observatory.css (reduzir animacoes / basemap
//   colorido). DOM criado LAZY no primeiro open (sem mudanca em main.ts);
//   fecha por botao, backdrop ou Esc. Estilos em observatory.css.
// =============================================================================

import { TOPBAR_OPERATOR, operatorInitials } from '../topbar/topbar-identity';
import { resolveOwnerIdentity } from '../../app/owner-identity';

/** Body classes aplicadas pelos switches (regras em observatory.css). */
export const REDUCE_MOTION_BODY_CLASS = 'u-pref-reduce-motion';
export const BASEMAP_COLOR_BODY_CLASS = 'u-pref-basemap-color';

/** Definicao de um switch do painel: rotulo + body class que ele controla. */
interface SettingsSwitchSpec {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly bodyClass: string;
}

/** Os 2 switches funcionais do painel minimo. */
const SETTINGS_SWITCHES: readonly SettingsSwitchSpec[] = [
  {
    id: 'settingsReduceMotion',
    label: 'Reduzir animações',
    hint: 'desliga pulsos e transições decorativas',
    bodyClass: REDUCE_MOTION_BODY_CLASS,
  },
  {
    id: 'settingsBasemapColor',
    label: 'Basemap colorido',
    hint: 'desfaz a dessaturação institucional do mapa',
    bodyClass: BASEMAP_COLOR_BODY_CLASS,
  },
];

/** HTML de uma linha de switch (reusa .u-switch global do unified.css). */
function settingsSwitchRowHtml(spec: SettingsSwitchSpec): string {
  return `<div class="u-settings-row">
      <div class="u-settings-row-text">
        <div class="u-settings-row-label">${spec.label}</div>
        <div class="u-settings-row-hint">${spec.hint}</div>
      </div>
      <div class="u-switch" id="${spec.id}" role="switch" aria-checked="false" tabindex="0" aria-label="${spec.label}"></div>
    </div>`;
}

/** HTML completo do painel (backdrop + card). */
function settingsPanelHtml(): string {
  return `<div class="u-settings-backdrop" data-settings-close></div>
  <div class="u-settings-card" role="dialog" aria-modal="true" aria-label="Configurações">
    <div class="u-settings-head">
      <span class="u-settings-title">Configurações</span>
      <button type="button" class="u-settings-close" data-settings-close aria-label="Fechar configurações"><i class="ti ti-x"></i></button>
    </div>
    <div class="u-settings-identity">
      <span class="u-settings-avatar">${operatorInitials()}</span>
      <div>
        <div class="u-settings-id-name">${TOPBAR_OPERATOR.name}</div>
        <div class="u-settings-id-meta">${TOPBAR_OPERATOR.role}</div>
        <div class="u-settings-id-gab">${resolveOwnerIdentity().name} · ${resolveOwnerIdentity().meta}</div>
      </div>
    </div>
    ${SETTINGS_SWITCHES.map(settingsSwitchRowHtml).join('')}
  </div>`;
}

/** Cria o host do painel uma unica vez (lazy) e liga os listeners. */
function ensureSettingsPanelHost(): HTMLElement {
  const existing = document.getElementById('settingsPanel');
  if (existing) return existing;
  const host = document.createElement('div');
  host.id = 'settingsPanel';
  host.className = 'u-settings-panel';
  host.innerHTML = settingsPanelHtml();
  document.body.appendChild(host);
  bindSettingsPanel(host);
  return host;
}

/** Liga fechar (backdrop/botao/Esc) e os switches de preferencia. */
function bindSettingsPanel(host: HTMLElement): void {
  host.addEventListener('click', (event) => {
    if ((event.target as HTMLElement).closest('[data-settings-close]')) closeSettingsPanel();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && host.classList.contains('is-open')) closeSettingsPanel();
  });
  SETTINGS_SWITCHES.forEach((spec) => bindSettingsSwitch(host, spec));
}

/** Um switch: click/teclado togglam a body class + aria-checked. */
function bindSettingsSwitch(host: HTMLElement, spec: SettingsSwitchSpec): void {
  const el = host.querySelector<HTMLElement>(`#${spec.id}`);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO SETTINGS_SWITCHES que gerou o markup */
  if (!el) return;
  const toggle = (): void => {
    const on = document.body.classList.toggle(spec.bodyClass);
    el.classList.toggle('on', on);
    el.setAttribute('aria-checked', on ? 'true' : 'false');
  };
  el.addEventListener('click', toggle);
  el.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggle();
  });
}

/**
 * Abre o painel de Configuracoes (cria o DOM no primeiro uso).
 *
 * @example openSettingsPanel(); // chamado pela acao 'settings' do account menu
 */
export function openSettingsPanel(): void {
  ensureSettingsPanelHost().classList.add('is-open');
}

/** Fecha o painel (backdrop/X/Esc). */
export function closeSettingsPanel(): void {
  document.getElementById('settingsPanel')?.classList.remove('is-open');
}
